// @flow
import * as React from 'react';

type UseAutoFocusArgs = {|
  /** If true, doe not focus the immediate child on mount */
  disableFocusOnMount?: boolean,
  /** If true, will not restore focus to a previous point */
  disableRestoreFocus?: boolean,
|};

type UseAutoFocusResult = [{ current: mixed | null }, () => boolean];

type FocusContext = null | (() => boolean);
/**
 * A context which provides a callback to focus on an element. Primary use case is to invoke a parent's focus function on unmount
 */
export const Focus = React.createContext<FocusContext>(null);

// Invokes first predicate that evaluates to true every 50ms to handle nested focuses in a single commit
// Document focus is a global state. It turns out a very good heuristic of what should be focused when React elements mount/unmount is to simply use the first focus function and ignore the rest

const timeouts = {};
const throttle = (predicate: () => boolean, key: string): mixed => {
  if (!timeouts[key] && predicate()) {
    timeouts[key] = setTimeout(() => {
      timeouts[key] = null;
    }, 50);
  }
};

/** A hook which provides a ref and a focus function. By default, the referenced element is focused on mount.  */
const useAutoFocus = ({
  disableFocusOnMount,
  disableRestoreFocus,
}: UseAutoFocusArgs): UseAutoFocusResult => {
  const container = React.useRef(null);
  const restoreParentFocus = React.useContext(Focus);

  const mounted = React.useRef(false);
  const focus = React.useCallback(() => {
    if (
      container.current &&
      mounted.current &&
      !disableFocusOnMount &&
      typeof container.current.focus === 'function'
    ) {
      (container.current: any).focus();
      // eslint-disable-next-line no-restricted-globals
      return document.activeElement === container.current;
    }
    return false;
  }, [container, disableFocusOnMount]);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    throttle(focus, 'autoFocus');
  }, [focus]);

  React.useEffect(
    () => () => {
      // Only focus parent if current element is unmounting
      if (restoreParentFocus && !mounted.current && !disableRestoreFocus) {
        //
        throttle(restoreParentFocus, 'restoreFocus');
      }
    },
    [restoreParentFocus, disableRestoreFocus],
  );

  return [container, focus];
};

export default useAutoFocus;
